'use client';
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button — green-acres spec (brief §3.8).
 * Primary = forest-green CTA. Radius 8px. Min tap target 44px (size md).
 * Backward-compatible variant names retained (navy, accent, glass, …).
 */
export const buttonVariants = cva(
  [
    'group relative inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-semibold leading-none select-none',
    'transition-[transform,background,color,box-shadow,border-color] duration-200 ease-premium',
    'disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-cta text-cta-foreground rounded-md shadow-sm hover:bg-cta-hover hover:shadow-md',
        default:
          'bg-cta text-cta-foreground rounded-md shadow-sm hover:bg-cta-hover hover:shadow-md',
        secondary:
          'bg-surface text-cta rounded-md border border-cta hover:bg-cta/[0.06]',
        outline:
          'rounded-md border border-border bg-transparent text-ink hover:border-ink hover:bg-surface-cream',
        ghost:
          'rounded-md text-ink hover:bg-surface-cream hover:text-ink',
        link:
          'text-cta underline-offset-4 hover:underline rounded-none',
        destructive:
          'bg-error text-error-foreground rounded-md hover:bg-error/90',
        danger:
          'bg-error text-error-foreground rounded-md hover:bg-error/90',
        'danger-ghost':
          'rounded-md text-ink-secondary hover:bg-error/10 hover:text-error',
        success:
          'bg-success text-success-foreground rounded-md hover:bg-success/90',
        'success-ghost': 'rounded-md text-success hover:bg-success/10',
        glass:
          'glass rounded-md text-ink ring-1 ring-white/40 hover:bg-white/90',
        // Legacy aliases (kept for backward compatibility)
        navy:
          'bg-primary text-primary-foreground rounded-md hover:bg-primary-700',
        accent:
          'bg-cta text-cta-foreground rounded-md shadow-sm hover:bg-cta-hover hover:shadow-md',
      },
      size: {
        sm: 'h-9 px-4 text-sm rounded-md',
        md: 'h-11 px-6 text-sm', // 44px tap target, 24px h-padding (brief)
        default: 'h-11 px-6 text-sm',
        lg: 'h-12 px-7 text-[15px]',
        xl: 'h-14 px-9 text-base',
        // Icon: circular 40px (brief §3.8)
        icon: 'h-10 w-10 p-0 rounded-full',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant, size, asChild = false, loading, icon, iconRight, children, className, disabled, ...props },
    ref,
  ) => {
    const Comp = (asChild ? Slot : 'button') as React.ElementType;
    return (
      <Comp
        ref={ref}
        disabled={asChild ? undefined : disabled || loading}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
            {children}
            {!loading && iconRight}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
